import React from 'react';
import { useUI } from '../contexts/UIContext';
import PullDownMenu from './PullDownMenu';

/**
 * Sayfa Düzeni Bileşeni
 * Tüm sayfalar için ortak düzen sağlar
 * 
 * @param {Object} props - Bileşen özellikleri
 * @param {ReactNode} props.children - Alt bileşenler
 * @param {boolean} props.disablePullDown - Aşağı çekme menüsünü devre dışı bırakır
 * @param {string} props.className - Ek CSS sınıfları
 * @param {string} props.title - Sayfa başlığı
 * @param {boolean} props.showBackButton - Geri düğmesini göster/gizle
 * @param {Function} props.onBackClick - Geri düğmesine tıklama olayı
 */
const PageLayout = ({ 
  children, 
  disablePullDown = false, 
  className = '', 
  title = '', 
  showBackButton = false,
  onBackClick 
}) => {
  const { 
    navigateToScreen, 
    handleTouchStart, 
    handleTouchMove, 
    handleTouchEnd, 
    showPullDownMenu, 
    closePullDownMenu,
    isPulling,
    pullDownProgress,
    showSecurityVerification
  } = useUI();

  // Dokunmatik olayların düzgün çalışması için handler'lar
  const handleTouchStartOptimized = (e) => {
    if (disablePullDown) return;
    handleTouchStart(e);
  };
  
  const handleTouchMoveOptimized = (e) => {
    if (disablePullDown) return;
    handleTouchMove(e);
  };
  
  const handleTouchEndOptimized = (e) => {
    if (disablePullDown) return;
    handleTouchEnd(e);
  };
  
  // Dokunmatik olayları
  const touchHandlers = disablePullDown ? {} : {
    onTouchStart: handleTouchStartOptimized,
    onTouchMove: handleTouchMoveOptimized,
    onTouchEnd: handleTouchEndOptimized,
    onMouseDown: handleTouchStartOptimized,
    onMouseMove: handleTouchMoveOptimized,
    onMouseUp: handleTouchEndOptimized
  };
  return (
    <div 
      className={`flex flex-col min-h-screen ${className} ${isPulling ? 'pull-active' : ''}`}
      {...touchHandlers}
      style={{
        touchAction: (pullDownProgress > 0 || showPullDownMenu) ? 'none' : 'auto',
      }}
    >
      {/* Pull-Down Göstergesi */}
      {!disablePullDown && pullDownProgress > 0 && !showPullDownMenu && (
        <div className="fixed top-0 left-0 right-0 flex justify-center z-30 pointer-events-none">
          <div 
            className="w-10 h-1 bg-gray-300 rounded-full mt-2 transition-opacity duration-200"
            style={{ opacity: pullDownProgress / 100 }}
          ></div>
        </div>
      )}
      
      {/* Aşağı Çekme Menüsü */}
      {!disablePullDown && showPullDownMenu && (
        <PullDownMenu 
          onItemSelect={navigateToScreen}
          onShowSecurityVerification={showSecurityVerification}
        />
      )}
      
      {/* Page Header with Title and Back Button if provided */}
      {(title || showBackButton) && (
        <div className="flex items-center p-4 border-b">
          {showBackButton && (
            <button 
              onClick={onBackClick || (() => window.history.back())}
              className="p-2 mr-3 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label="Back"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" 
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
            </button>
          )}
          {title && <h1 className="text-xl font-bold">{title}</h1>}
        </div>
      )}
      
      {/* Sayfa İçeriği */}
      {children}
    </div>
  );
};

export default PageLayout;